// src/App.js (con rutas actualizadas)
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, Outlet, useSearchParams } from 'react-router-dom';
import { ThemeProvider, Typography } from '@mui/material';
import theme from './theme';
import ErrorBoundary from './components/ErrorBoundary';
import AppProviders from './context/AppProviders';

/* ───── Layouts ──── */
import MainLayout from './components/MainLayout';

/* ───── Páginas genéricas ──── */
import AdminPage          from './pages/AdminPage';
import PricingPage        from './pages/PricingPage';


/* ───── Eventos y atletas ──── */
import EventHeats         from './components/Eventos/EventsHeats/EventHeats';
import HeatDetails        from './components/Eventos/HeatsDetails';
import AthleteEvents      from './pages/Atletas/AthleteEvents';

/* ───── Competencias (gestión) ──── */
import NuevaCompetencia          from './pages/gestion/modalNuevaCompetencia';
import ConfigurationCompetencia  from './pages/gestion/ConfigurationCompetencia';

/* ───── Otras utilidades ──── */
import StatsOrganizador  from './components/eventos-atletismo/statsOrganizador';
import EventPage         from './pages/EventPage';
import TableroVentana    from './components/Tableros/Velocidad/TableroVentana';
import HorizontalScoreboard from './components/Tableros/Campo/HorizontalScoreboard';
import VerticalScoreboard from './components/Tableros/Campo/VerticalScoreboard';
import CompetitionsPublicList from './pages/public/CompetitionsPublicList.jsx';

/* ───── Auth & perfiles ──── */
import LoginPage           from './pages/auth/login';
import RegisterPage        from './pages/auth/register';
import Dashboard           from './pages/dashboard';
import TrainingPage        from './pages/training';
import EventsPageAthlete   from './pages/events';
import ProfilePage         from './pages/profile';
import HistoryPage         from './pages/history';
import ProtectedRoute      from './components/Common/ProtectedRoute';
import UnauthorizedPage    from './pages/UnauthorizedPage';
import AdminAthleteProfile from './pages/profile/AdminAthleteProfile.js';
import ProfileHome from './pages/profile/ProfileHome';
import AthleteEventProfile      from './components/Athletes/AthleteEventProfile';
import TeamEventProfile from './components/Teams/TeamEventProfile';

/* ───── Context específico de competencia ──── */
import { EventProvider } from './context/EventContext';

/* ═════════════════════════════════════════════════════════════ */
/*  Layout que envuelve TODO lo referente a una competencia      */
/* ═════════════════════════════════════════════════════════════ */
const CompetitionLayout = () => {
  const { competitionId } = useParams();   // viene de la ruta /:competitionId/*
  return (
    <EventProvider competitionId={competitionId}>
      <MainLayout>
        <Outlet />
      </MainLayout>
    </EventProvider>
  );
};

const ScoreboardView = () => {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type') || 'track';   // ‘track’ por defecto
  const id   = searchParams.get('id')   || 'currentConfig';

  // Cada tablero puede leer el `id` para cargar la configuración
  // desde localStorage (o desde donde prefieras).
  switch (type) {
    case 'horizontalField':
      return <HorizontalScoreboard configId={id} />;
    case 'verticalField':
      return <VerticalScoreboard   configId={id} />;
    case 'track':
    default:
      return <TableroVentana       configId={id} />;
  }
};

const App = () => (
  <ErrorBoundary>
    <AppProviders>  {/*  ya NO crea EventProvider */}
      <ThemeProvider theme={theme}>
        <Router>
          <Routes>
            {/* ─────────────────── RUTAS SIN COMPETENCIA ─────────────────── */}
            <Route path="/" element={<MainLayout />}>
              {/* públicas */}
              <Route path="admin"       element={<AdminPage />} />
              <Route path="precios"               element={<PricingPage />} />
              <Route path="eventos"               element={<CompetitionsPublicList />} />

              {/* perfiles vía QR 
              <Route path="perfil/atleta/:athleteId" element={<AthleteEventProfile />} />*/}

              {/* auth-free training demo */}
              <Route path="training" element={<TrainingPage />} />

              {/* protegidas para organizador */}
              <Route element={<ProtectedRoute roles={['organizador']} />}>
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="events"    element={<EventsPageAthlete />} />
                <Route path="profile"   element={<ProfilePage />} />
                <Route path="/perfil" element={<ProfileHome />} />
                <Route path="history"   element={<HistoryPage />} />

                {/* sistema de inscripciones */}
                <Route path="ajustes/stats-organizador" element={<StatsOrganizador />} />
                <Route path="/"      element={<Navigate to="/inicio" replace />} />
                <Route path="inicio" element={<NuevaCompetencia />} />
              </Route>

              {/* página de evento (marketing) */}
              <Route path="/:slug/*" element={<EventPage />} />

              {/* 401 / 403 */}
              <Route path="unauthorized" element={<UnauthorizedPage />} />
            </Route>

            {/* ──────────── RUTAS DE COMPETENCIA ──────────── */}
            <Route path="/:competitionId/*" element={<CompetitionLayout />}>
            
              {/* Configuración general (tabs de ajustes, inscripciones, etc.) */}
              <Route path=":tab/*" element={<ConfigurationCompetencia />} />
              
            
                
              {/* ---------- ACCESOS DIRECTOS ---------- */}
              {/* editable (oficiales) */}
              <Route path="event/:eventNumber/:round"                         element={<EventHeats />} />
              <Route path="event/:eventNumber/:round/:heat/details"           element={<HeatDetails />} />
                
              {/* público / coaches (solo lectura) */}
              <Route path="eventos/:eventNumber/:round"                       element={<EventHeats readOnly />} />
                
              {/* perfiles de atleta */}
              <Route path="athlete/:id"                                       element={<AthleteEventProfile />} />
              <Route path="team/:teamName" element={<TeamEventProfile />} />
            </Route>
            {/* ─────────────────── AUTENTICACIÓN ─────────────────── */}
            <Route path="auth/login"    element={<LoginPage />} />
            <Route path="auth/register" element={<RegisterPage />} />

            <Route
              path="eventos/:eventNumber/:round"
              element={<EventHeats readOnly />}
            />

            {/* ─────────────────── TABLERO EXTERNO ─────────────────── */}
            <Route path="scoreboard" element={<ScoreboardView />} />
            <Route path="/horizontal-scoreboard" element={<HorizontalScoreboard />} />
            <Route path="/vertical-scoreboard" element={<VerticalScoreboard />} />

            {/* ─────────────────── 404 ─────────────────── */}
            <Route
              path="*"
              element={
                <Typography variant="h6" color="error" align="center" sx={{ mt: 8 }}>
                  404 – Página no encontrada
                </Typography>
              }
            />
          </Routes>
        </Router>
      </ThemeProvider>
    </AppProviders>
  </ErrorBoundary>
);

export default App;
